import { useState, useEffect } from 'react';
import { X, Download, Share, Plus, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

// Check if running in standalone mode
const checkIsStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');
};

// Check device type
const checkDeviceType = () => {
  if (typeof window === 'undefined') return { isIOS: false, isAndroid: false };
  const userAgent = window.navigator.userAgent.toLowerCase();
  return {
    isIOS: /iphone|ipad|ipod/.test(userAgent) && !window.MSStream,
    isAndroid: /android/.test(userAgent)
  };
};

// Check if previously dismissed
const checkDismissed = () => {
  if (typeof window === 'undefined') return false;
  const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
  if (dismissedTime) {
    const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
    return hoursSinceDismissed < 24;
  }
  return false;
};

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  const deviceType = checkDeviceType();
  const isIOS = deviceType.isIOS;
  const isAndroid = deviceType.isAndroid;
  const isStandalone = checkIsStandalone();
  const [dismissed, setDismissed] = useState(checkDismissed);

  useEffect(() => {
    // Don't run on server or if already installed/dismissed
    if (isStandalone || dismissed) return;

    // Listen for beforeinstallprompt (Chrome, Edge, Samsung Browser)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay (better UX)
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS - show prompt after delay since there's no beforeinstallprompt event
    let iosTimer;
    if (isIOS) {
      iosTimer = setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    }

    // Listen for successful install
    const handleInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      console.log('PWA installed successfully');
    };
    
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [isStandalone, dismissed, isIOS]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Show native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted install');
      }
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Don't show if already installed or dismissed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-card border border-border rounded-2xl shadow-2xl p-4 max-w-md mx-auto">
        {/* Close button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-secondary transition-colors"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>

        <div className="flex items-start gap-3">
          {/* App icon */}
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
            <Smartphone className="w-6 h-6 text-white" />
          </div>

          <div className="flex-1 min-w-0 pr-6">
            <h3 className="font-semibold text-sm">Install FlipValue</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add to home screen for the best experience
            </p>
          </div>
        </div>

        {/* iOS Instructions */}
        {isIOS && (
          <div className="mt-3 p-3 bg-secondary/50 rounded-xl">
            <p className="text-xs text-muted-foreground mb-2">To install:</p>
            <div className="flex items-center gap-2 text-xs">
              <span className="flex items-center gap-1">
                <Share className="w-4 h-4 text-blue-500" />
                Tap Share
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="flex items-center gap-1">
                <Plus className="w-4 h-4 text-primary" />
                Add to Home Screen
              </span>
            </div>
          </div>
        )}

        {/* Android/Desktop Install Button */}
        {!isIOS && deferredPrompt && (
          <div className="mt-3 flex gap-2">
            <Button 
              onClick={handleInstall}
              size="sm"
              className="flex-1 gap-2"
            >
              <Download className="w-4 h-4" />
              Install App
            </Button>
            <Button 
              onClick={handleDismiss}
              variant="outline"
              size="sm"
            >
              Not now
            </Button>
          </div>
        )}

        {/* Android without prompt (fallback) */}
        {isAndroid && !deferredPrompt && (
          <div className="mt-3 p-3 bg-secondary/50 rounded-xl">
            <p className="text-xs text-muted-foreground">
              Tap the menu (⋮) and select &quot;Add to Home screen&quot;
            </p>
          </div>
        )}

        {/* Features */}
        <div className="mt-3 flex gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Faster access
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Works offline
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Full screen
          </span>
        </div>
      </div>
    </div>
  );
}
